import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QrCode, Receipt, CalendarPlus, CalendarClock, Download, Video, BookOpen, UserPlus, LogOut } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import InviteColleagueModal from "@/components/InviteColleagueModal";
import { downloadIcs } from "@/pages/portal/event-space/shared";
import PendingPaymentActions from "@/components/PendingPaymentActions";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Row({ icon: Icon, title, desc, children, testId, disabled }) {
  return (
    <li className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-neutral-100 bg-white p-4 ${disabled ? "opacity-60" : ""}`} data-testid={testId}>
      <div className="flex items-start gap-3"><span className="grid h-10 w-10 place-items-center rounded-xl bg-wine-50 text-wine-600"><Icon className="h-5 w-5" /></span><div><div className="font-bold text-navy">{title}</div><div className="text-xs text-neutral-500">{desc}</div></div></div>
      {children}
    </li>
  );
}
const btn = "inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white hover:bg-wine-600 disabled:opacity-50";

export default function Documents({ space, slug }) {
  const ev = space.event;
  const reg = space.registration;
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const nav = useNavigate();
  const active = reg && !["cancelled"].includes(space.state);
  const cancelReg = async () => {
    if (!window.confirm("Confirmer votre désinscription ? Vos rendez-vous éventuels seront annulés.")) return;
    try {
      const { data } = await api.delete(`/my/event-registration/${ev.id}`);
      toast.success(data.cancelled_meetings > 0 ? `Désinscription confirmée — ${data.cancelled_meetings} RDV annulé(s)` : "Désinscription confirmée");
      window.dispatchEvent(new Event("event-spaces:refresh"));
      nav(`/events/${slug}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const ticketOk = reg && space.state === "eligible";
  const exportMeetings = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/event-space/${slug}/agenda`);
      const booked = r.data.cells.filter((c) => c.state === "booked");
      if (!booked.length) { toast.info("Aucun rendez-vous confirmé à exporter"); return; }
      downloadIcs(`mes-rdv-${slug}.ics`, booked.map((c) => ({ uid: c.meeting_id, title: `RDV ${c.with_name} — ${ev.title}`, start: c.start, end: c.end, location: c.location || ev.location })));
    } finally { setBusy(false); }
  };
  return (
    <ul className="space-y-3" data-testid="documents-section">
      {space.state === "payment_pending" && reg?.id && !space.exhibitor && <li><PendingPaymentActions registration={{ currency: "eur", ...reg }} eventId={ev.id} onCancelled={() => window.location.reload()} /></li>}
      <Row icon={QrCode} title="Mon billet" testId="doc-ticket" disabled={!ticketOk}
        desc={ticketOk ? "PDF avec QR code d'accès" : space.state === "payment_pending" ? "Disponible après finalisation du paiement" : space.state === "cancelled" ? "Inscription annulée — billet invalide" : space.exhibitor ? "Accès exposant : présentez-vous à l'accueil exposants" : "Disponible après inscription"}>
        {ticketOk && <a href={`${BACKEND}/api/registrations/${reg.id}/badge`} target="_blank" rel="noreferrer" className={btn} data-testid="doc-ticket-download"><Download className="h-4 w-4" />Télécharger</a>}
      </Row>
      {active && ev.virtual_link && (
        <Row icon={Video} title={ev.virtual_link_label || "Lien de connexion"} desc={ev.virtual_password ? `Mot de passe : ${ev.virtual_password}` : "Événement en ligne"} testId="doc-virtual">
          <a href={ev.virtual_link} target="_blank" rel="noopener noreferrer" className={btn} data-testid="doc-virtual-link"><Video className="h-4 w-4" />Rejoindre</a>
        </Row>
      )}
      <Row icon={BookOpen} title="Catalogue de l'événement" desc="Exposants et programme (PDF)" testId="doc-catalog">
        <a href={`${BACKEND}/api/events/${ev.id}/catalog.pdf`} target="_blank" rel="noopener noreferrer" className={btn} data-testid="doc-catalog-download"><Download className="h-4 w-4" />Télécharger</a>
      </Row>
      <Row icon={Receipt} title="Ma facture" desc={reg?.price ? "Facture correspondant à la commande (visible par le titulaire et son entreprise)" : "Inscription gratuite — pas de facture"} testId="doc-invoice" disabled={!reg?.price}>
        {reg?.price > 0 && <Link to="/portal/my-company" className={btn} data-testid="doc-invoice-link"><Download className="h-4 w-4" />Mes factures</Link>}
      </Row>
      <Row icon={CalendarPlus} title="Ajouter l'événement à mon calendrier" desc="Fichier .ics compatible Google, Outlook, Apple" testId="doc-event-ics">
        <button onClick={() => downloadIcs(`${slug}.ics`, [{ uid: `event-${ev.id}`, title: ev.title, start: ev.date, end: ev.end_date, location: ev.address || ev.location, description: `${window.location.origin}/portal/event/${slug}/overview` }])} className={btn} data-testid="doc-event-ics-btn"><CalendarPlus className="h-4 w-4" />Ajouter</button>
      </Row>
      <Row icon={CalendarClock} title="Mon agenda de rendez-vous" desc="Récapitulatif des rencontres confirmées (.ics) — réexportez après chaque modification" testId="doc-meetings-ics">
        <button onClick={exportMeetings} disabled={busy} className={btn} data-testid="doc-meetings-ics-btn"><Download className="h-4 w-4" />Exporter</button>
      </Row>
      {active && (
        <Row icon={UserPlus} title="Inviter un collègue" desc="Il reçoit un e-mail pour confirmer son inscription" testId="doc-invite">
          <button onClick={() => setInviteOpen(true)} className={btn} data-testid="invite-colleague-btn"><UserPlus className="h-4 w-4" />Inviter</button>
        </Row>
      )}
      {active && reg.status !== "pending_payment" && (
        <li className="pt-2">
          <button onClick={cancelReg} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-xs font-semibold text-neutral-600 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700" data-testid="cancel-registration"><LogOut className="h-3.5 w-3.5" />Me désinscrire</button>
        </li>
      )}
      {inviteOpen && <InviteColleagueModal event={ev} onClose={() => setInviteOpen(false)} onInvited={() => { setInviteOpen(false); toast.success("Invitation envoyée"); }} />}
    </ul>
  );
}
